import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock } from "lucide-react";
import api from "../lib/api";
import VideoCard from "../components/VideoCard";
import { SkeletonRow } from "../components/Skeleton";
import { useAuth } from "../context/AuthContext";

/**
 * WatchHub — YouTube-feel browse grid for Tap In TV.
 *
 *   • Continue-watching rail at the top (signed-in users only) — pulls
 *     /api/history so each viewer immediately sees their resume queue.
 *   • Sticky filter chips + search input.
 *   • Infinite-scroll 4-up grid (loads 60 more rows on scroll-to-bottom
 *     via IntersectionObserver — bounded by `hasMore`).
 *
 * Mounted at:
 *   /watch           — top-level web/mobile/desktop grid
 *   /tv?tab=watch    — embedded inside TvApp
 */

const FILTERS = [
  { key: "all",       label: "All",       params: { sort: "recent" } },
  { key: "live",      label: "Live Now",  params: { live: true } },
  { key: "trending",  label: "Trending",  params: { sort: "trending" } },
  { key: "films",     label: "Films",     params: { category: "films" } },
  { key: "music",     label: "Music",     params: { category: "music" } },
  { key: "sports",    label: "Sports",    params: { category: "sports" } },
  { key: "gospel",    label: "Gospel",    params: { category: "gospel" } },
  { key: "cooking",   label: "Cooking",   params: { category: "cooking" } },
  { key: "news",      label: "News",      params: { category: "news" } },
  { key: "kids",      label: "Kids",      params: { category: "kids" } },
];

const PAGE_SIZE = 24;

export default function WatchHub({ embedded = false }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialFilter = params.get("filter") || "all";
  const initialQuery = params.get("q") || "";
  const [filter, setFilter] = useState(initialFilter);
  const [q, setQ] = useState(initialQuery);
  const [items, setItems] = useState(null);
  const [continueItems, setContinueItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [err, setErr] = useState("");
  const searchDebounceRef = useRef(null);
  const sentinelRef = useRef(null);

  const activeFilter = useMemo(
    () => FILTERS.find((f) => f.key === filter) || FILTERS[0],
    [filter],
  );

  // Sync URL on filter / search change (skipped when embedded inside TvApp
  // because TvApp owns the search params).
  useEffect(() => {
    if (embedded) return;
    const next = new URLSearchParams();
    if (filter && filter !== "all") next.set("filter", filter);
    if (q) next.set("q", q);
    setParams(next, { replace: true });
  }, [filter, q, embedded, setParams]);

  // Continue-watching rail — fetched once per session per signed-in user.
  // Silently no-ops for guests (history endpoint requires auth).
  useEffect(() => {
    if (!user) { setContinueItems([]); return; }
    let cancelled = false;
    api.get("/history").then((r) => {
      if (cancelled) return;
      const list = (r.data || [])
        .filter((v) => v && v.id && v.progress_sec > 5)
        .slice(0, 12);
      setContinueItems(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // First page (or full reset) when filter / query changes.
  useEffect(() => {
    let cancelled = false;
    const fetchFirstPage = async () => {
      setLoading(true);
      setErr("");
      setHasMore(true);
      try {
        const apiParams = { ...activeFilter.params, limit: PAGE_SIZE, skip: 0 };
        if (q.trim()) apiParams.q = q.trim();
        const r = await api.get("/videos", { params: apiParams });
        if (cancelled) return;
        const data = r.data || [];
        setItems(data);
        if (data.length < PAGE_SIZE) setHasMore(false);
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || "Couldn't load videos.");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(fetchFirstPage, 250);
    return () => { cancelled = true; };
  }, [activeFilter, q]);

  // Load-more handler used by IntersectionObserver and (as a fallback) the
  // explicit "Show more" button at the end of the grid.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !items) return;
    setLoadingMore(true);
    try {
      const apiParams = { ...activeFilter.params, limit: PAGE_SIZE, skip: items.length };
      if (q.trim()) apiParams.q = q.trim();
      const r = await api.get("/videos", { params: apiParams });
      const data = r.data || [];
      // Dedupe in case of overlapping skip windows.
      const existingIds = new Set(items.map((it) => it.id));
      const fresh = data.filter((it) => !existingIds.has(it.id));
      setItems([...items, ...fresh]);
      if (data.length < PAGE_SIZE) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, items, activeFilter, q]);

  // IntersectionObserver — fire loadMore when sentinel scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) loadMore();
    }, { rootMargin: "400px 0px" });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hasMore, loadMore]);

  return (
    <div
      data-testid="watch-hub"
      className={embedded ? "" : "min-h-screen pt-24 pb-24 bg-[#0A0A0A]"}
    >
      <div className={embedded ? "px-6" : "px-4 sm:px-6 md:px-10 max-w-[1600px] mx-auto"}>
        <header className="mb-5">
          <h1 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tighter">
            Watch on Tap IN TV
          </h1>
          <p className="text-white/55 text-sm mt-1">
            Films, music, live shows, sports, gospel, cooking — the entire network in one feed.
          </p>
        </header>

        {/* Continue Watching rail — only when there's something to resume. */}
        {continueItems.length > 0 && (
          <section data-testid="watch-continue-rail" className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-tv-accent" />
              <h2 className="font-display font-bold text-white text-lg">Continue Watching</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory no-scrollbar">
              {continueItems.map((it) => {
                const pct = it.duration_sec ? Math.min(100, (it.progress_sec / it.duration_sec) * 100) : 0;
                return (
                  <div
                    key={it.id}
                    data-testid={`watch-continue-${it.id}`}
                    className="shrink-0 w-[260px] sm:w-[300px] md:w-[340px] snap-start"
                  >
                    <VideoCard item={it} variant="wide" live={it.is_live} progressPct={pct} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Search + filter chips */}
        <div className="sticky top-20 z-20 bg-[#0A0A0A]/95 backdrop-blur-md py-3 -mx-4 sm:-mx-6 md:-mx-10 px-4 sm:px-6 md:px-10 border-b border-white/5 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search films, creators, shows…"
              data-testid="watch-search"
              data-tv-focus="true"
              className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white outline-none focus:border-tv-accent flex-1 min-w-[180px]"
            />
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  data-testid={`watch-filter-${f.key}`}
                  data-tv-focus="true"
                  aria-pressed={f.key === filter}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition ${
                    f.key === filter
                      ? "bg-tv-accent text-black border-tv-accent shadow-[0_0_12px_rgba(255,212,0,0.3)]"
                      : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {err && (
          <div data-testid="watch-error" className="text-red-300 text-sm py-4">{err}</div>
        )}

        {loading && (!items || items.length === 0) && <SkeletonRow />}

        {!loading && items?.length === 0 && (
          <div data-testid="watch-empty" className="text-white/50 text-sm py-12 text-center">
            No videos match those filters. Try a different category or clear the search.
          </div>
        )}

        {items && items.length > 0 && (
          <>
            <div
              data-testid="watch-grid"
              className="grid gap-x-4 gap-y-8 sm:gap-x-5 sm:gap-y-10 grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            >
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`watch-card-${it.id}`}
                  className="flex flex-col gap-2"
                >
                  {/* Card thumbnail (16:9, no overlay text — titles render below) */}
                  <VideoCard item={it} variant="wide" live={it.is_live} fullWidth hideMeta />
                  {/* Title + creator BELOW the card so titles don't bleed
                      into the next row in dense YouTube-style layouts. */}
                  <div className="px-1">
                    <div className="font-display font-bold text-white text-sm leading-tight line-clamp-2">
                      {it.title}
                    </div>
                    <div className="text-xs text-white/55 mt-1 truncate">
                      {it.creator_name}{it.view_count ? ` · ${formatViews(it.view_count)} views` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Infinite-scroll sentinel + state */}
            <div ref={sentinelRef} className="h-12 flex items-center justify-center mt-4">
              {loadingMore && (
                <div data-testid="watch-loading-more" className="text-white/40 text-xs uppercase tracking-widest">Loading more…</div>
              )}
              {!hasMore && items.length > PAGE_SIZE && (
                <div className="text-white/30 text-xs uppercase tracking-widest">You're all caught up</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Human-friendly view count: 1.2K · 14K · 1.4M · 28M */
function formatViews(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(v);
}
